import { getCitationSourceApp } from "#features/conversations/components/conversation-source/citationSource.tsx";
import { useCdnAssets } from "#hooks/useCdnAssets.ts";
import type { ConversationSource } from "#types/types.ts";
import { useColorSchemeContext } from "@hopper-ui/styled-system";
import classNames from "classnames";
import "#css/ink.css";
import "./CitationPill.css";

export type CitationPillTone = "default" | "onDark";

interface Props {
    source: ConversationSource;
    /** The number beside the mark. Omitted, the pill shows the mark alone, as a square. */
    displayNumber?: number;
    /** Shows the hover state while something outside the pill points at it (its sources row). */
    isActive?: boolean;
    /** `onDark` takes the white-on-navy pair for a dark band in a host page. */
    tone?: CitationPillTone;
}

/**
 * A citation marker: the source app's monochrome mark, and its number on a surface with a sources
 * chip. Grey at rest; hovered, it steps up a shade, and hovered or focused, the mark crossfades to the
 * app's own colours. The wrapper's link or row drives both through CSS.
 */
export const CitationPill = ({ source, displayNumber, isActive, tone = "default" }: Props) => {
    const { getCdnAssetUrl } = useCdnAssets();
    const { colorScheme } = useColorSchemeContext();
    const { glyph, brandMark: lightBrandMark, brandMarkDark } = getCitationSourceApp(source);
    const brandMark = colorScheme === "dark" ? brandMarkDark : lightBrandMark;

    return (
        <span
            className={classNames("wai-citation-pill", {
                "wai-citation-pill--mark-only": displayNumber === undefined,
                "wai-citation-pill--on-dark": tone === "onDark"
            })}
            data-active={isActive || undefined}
        >
            <span aria-hidden="true" className={classNames("wai-citation-pill-mark", { "wai-citation-pill-mark--branded": brandMark })}>
                <span className="wai-citation-pill-mark-mono">{glyph}</span>
                {brandMark && <img className="wai-citation-pill-mark-brand" src={getCdnAssetUrl(brandMark)} alt="" />}
            </span>
            {displayNumber}
        </span>
    );
};
